import { difference, get } from 'lodash'
import { useCallback, useState } from 'react'
import { DefaultSeparator } from '../constants'
import {
  normalizeJsonArray,
  parseAndCheckData,
} from './useDataLoaderUtils/parser'
import { stackData } from './useDataLoaderUtils/stack'
import useDataParser from './useDataParser'
import useDataTransforms from './useDataTransforms'

export const DATA_LOADER_MODE = {
  DIRECT: 'direct',
  REPLACE: 'replace',
}

const __cache = {}

export default function useDataLoader() {
  /* First stage: raw user input */
  const [userInput, setUserInput] = useState('')
  const [dataSource, setDataSource] = useState(null)

  /* Second stage: parsed data and user data type (csv, json, …) */
  const [userData, setUserData] = useState(null)
  const [userDataType, setUserDataType] = useState(null)
  const [parseError, setParserError] = useState(null)

  /* Parsing options */
  const [separator, setSeparator] = useState(DefaultSeparator)
  const [thousandsSeparator, setThousandsSeparator] = useState(',')
  const [decimalsSeparator, setDecimalsSeparator] = useState('.')
  const [locale, setLocale] = useState(navigator.language || 'en-US')

  /* Replace mode */
  const [mode, setMode] = useState(DATA_LOADER_MODE.DIRECT)
  const [replaceRequiresConfirmation, setReplaceRequiresConfirmation] = useState(undefined)

  /* Parsing engine */
  const { data, setData, loading, parseDatasetAuto, parseDatasetAndSetData } = useDataParser()

  /* Data transforms (stack / transpose) */
  const {
    stackDimension,
    setStackDimension,
    unstackedData,
    unstackedColumns,
    setUnstackedInfo,
    handleStackOperation,
    handleTranspose,
  } = useDataTransforms({
    userData,
    setUserData,
    data,
    parseDatasetAndSetData,
    locale,
    decimalsSeparator,
    thousandsSeparator,
  })

  const columnsTypes = unstackedColumns ?? data?.dataTypes

  // ─── Option-change handlers ───────────────────────────────────────────────

  function handleChangeSeparator(newSeparator) {
    const [dataType, parsedUserData, error] = parseAndCheckData(userInput, {
      separator: newSeparator,
    })
    setSeparator(newSeparator)
    setUserDataType(dataType)
    setParserError(error)
    if (dataType !== 'json' && !error && newSeparator) {
      setUserData(parsedUserData)
      parseDatasetAndSetData(parsedUserData, undefined, {
        locale,
        decimal: decimalsSeparator,
        group: thousandsSeparator,
      })
    }
  }

  function handleChangeLocale(newLocale) {
    if (!data) return
    parseDatasetAndSetData(data.dataset, data.dataTypes, {
      locale: newLocale,
      decimal: decimalsSeparator,
      group: thousandsSeparator,
    })
    setLocale(newLocale)
  }

  function handleChangeDecimalSeparator(newDecimalSeparator) {
    const [dataType, parsedUserData, error] = parseAndCheckData(userInput, {
      separator,
    })
    setDecimalsSeparator(newDecimalSeparator)
    setUserDataType(dataType)
    setParserError(error)
    if (dataType !== 'json' && !error) {
      setUserData(parsedUserData)
      parseDatasetAndSetData(parsedUserData, undefined, {
        locale,
        decimal: newDecimalSeparator,
        group: thousandsSeparator,
      })
    }
  }

  function handleChangeThousandsSeparator(newThousandsSeparator) {
    const [dataType, parsedUserData, error] = parseAndCheckData(userInput, {
      separator,
    })
    setThousandsSeparator(newThousandsSeparator)
    setUserDataType(dataType)
    setParserError(error)
    if (dataType !== 'json' && !error) {
      setUserData(parsedUserData)
      parseDatasetAndSetData(parsedUserData, undefined, {
        locale,
        decimal: decimalsSeparator,
        group: newThousandsSeparator,
      })
    }
  }

  // ─── Replace flow ─────────────────────────────────────────────────────────

  const handleReplacingData = useCallback(
    (nextUserData) => {
      parseDatasetAuto(nextUserData, undefined, {
        locale,
        decimal: decimalsSeparator,
        group: thousandsSeparator,
      }).then((newDataInferred) => {
        if (newDataInferred.errors.length > 0) {
          __cache.replacedData = newDataInferred
          setReplaceRequiresConfirmation('parse-error')
          return
        }

        const oldColNames = Object.keys(columnsTypes)
        const newColNames = Object.keys(newDataInferred.dataTypes)
        const missingCols = difference(oldColNames, newColNames)

        if (missingCols.length > 0) {
          __cache.replacedData = newDataInferred
          setReplaceRequiresConfirmation('missing-column:' + missingCols[0])
          return
        }

        const nextDataTypes = {
          ...newDataInferred.dataTypes,
          ...columnsTypes,
        }
        parseDatasetAuto(nextUserData, nextDataTypes, {
          locale,
          decimal: decimalsSeparator,
          group: thousandsSeparator,
        }).then((newData) => {
          if (newData.errors.length > 0) {
            __cache.replacedData = newDataInferred
            setReplaceRequiresConfirmation('type-mismatch')
            return
          }

          if (stackDimension) {
            setUnstackedInfo([nextUserData, newData.dataTypes])
            const stackedData = stackData(nextUserData, stackDimension)
            setUserData(stackedData)
            parseDatasetAuto(stackedData, data.dataTypes, {
              locale,
              decimal: decimalsSeparator,
              group: thousandsSeparator,
            }).then((result) => setData(result))
          } else {
            setData(newData)
          }
        })
      })
    },
    [
      columnsTypes,
      data,
      decimalsSeparator,
      locale,
      parseDatasetAuto,
      setData,
      setUnstackedInfo,
      setUserData,
      stackDimension,
      thousandsSeparator,
    ]
  )

  const startDataReplace = useCallback(() => {
    setMode(DATA_LOADER_MODE.REPLACE)
    __cache.userInput = userInput
    __cache.userDataType = userDataType
    __cache.dataSource = dataSource
    __cache.userData = userData
    setUserInput(null)
    setUserDataType(null)
    setDataSource(null)
    setUserData(null)
  }, [dataSource, userData, userDataType, userInput])

  const cancelDataReplace = useCallback(() => {
    setReplaceRequiresConfirmation(false)
    setMode(DATA_LOADER_MODE.DIRECT)
    setUserInput(__cache.userInput)
    setUserDataType(__cache.userDataType)
    setDataSource(__cache.dataSource)
    setUserData(__cache.userData)
  }, [])

  const commitDataReplace = useCallback(() => {
    setReplaceRequiresConfirmation(false)
    setData(__cache.replacedData)
    setParserError(null)
    setStackDimension(null)
    setUnstackedInfo([null, null])
  }, [setData, setStackDimension, setUnstackedInfo])

  // ─── Data ingestion ───────────────────────────────────────────────────────

  const handleNewUserData = useCallback(
    (nextUserData, forcedDataTypes = undefined) => {
      setUserData(nextUserData)
      if (mode === DATA_LOADER_MODE.DIRECT) {
        parseDatasetAndSetData(nextUserData, forcedDataTypes, {
          locale,
          decimal: decimalsSeparator,
          group: thousandsSeparator,
        })
      } else if (mode === DATA_LOADER_MODE.REPLACE) {
        handleReplacingData(nextUserData)
      } else {
        console.error('Unknown data loader mode!')
      }
    },
    [
      decimalsSeparator,
      handleReplacingData,
      locale,
      mode,
      parseDatasetAndSetData,
      thousandsSeparator,
    ]
  )

  function setUserDataAndDetect(str, source, options) {
    const [dataType, parsedUserData, error, extra] = parseAndCheckData(str, {
      separator: get(options, 'separator', null),
    })
    setUserInput(str)
    setDataSource(source)
    setUserDataType(dataType)
    setParserError(error)
    if (extra && typeof extra === 'object' && 'separator' in extra) {
      setSeparator(extra.separator)
    }
    if (dataType !== 'json' && !error) {
      handleNewUserData(parsedUserData)
    }
    const jsonPath = dataSource?.jsonPath ?? undefined
    if (dataType === 'json' && !error && jsonPath !== undefined) {
      const jsonData = get(parsedUserData, jsonPath, null)
      if (Array.isArray(jsonData)) {
        handleNewUserData(jsonData)
      }
    }
  }

  const setJsonData = useCallback(
    (nextData, path) => {
      const normalized = normalizeJsonArray(nextData)
      setUserData(normalized)
      setDataSource({ ...dataSource, jsonPath: path })
      handleNewUserData(normalized)
    },
    [dataSource, handleNewUserData]
  )

  function coerceTypes(nextTypes) {
    parseDatasetAndSetData(userData, nextTypes, {
      locale,
      decimal: decimalsSeparator,
      group: thousandsSeparator,
    })
  }

  function loadSample(rawData, sampleSeparator, sampleMeta) {
    setSeparator(sampleSeparator)
    setUserDataAndDetect(rawData, { type: 'sample', ...sampleMeta }, { separator: sampleSeparator })
  }

  function handleInlineEdit(newDataset) {
    setUserData(newDataset)
    parseDatasetAndSetData(newDataset, data.dataTypes, {
      locale,
      decimal: decimalsSeparator,
      group: thousandsSeparator,
    })
  }

  // ─── Project hydration / reset ────────────────────────────────────────────

  const reset = useCallback(() => {
    setData(null)
    setUserData(null)
    setUserDataType(null)
    setUserInput('')
    setDataSource(null)
    setParserError(null)
    setStackDimension(null)
    setUnstackedInfo([null, null])
  }, [setData, setStackDimension, setUnstackedInfo])

  const hydrateFromSavedProject = useCallback(
    (project) => {
      const {
        userInput,
        userData,
        userDataType,
        parseError,
        unstackedColumns,
        unstackedData,
        dataTypes,
        separator,
        thousandsSeparator,
        decimalsSeparator,
        locale,
        stackDimension,
        dataSource,
      } = project
      setUserInput(userInput)
      setUserDataType(userDataType)
      setSeparator(separator)
      setThousandsSeparator(thousandsSeparator)
      setDecimalsSeparator(decimalsSeparator)
      setLocale(locale)
      setStackDimension(stackDimension)
      setDataSource(dataSource)
      setUserData(userData)
      setParserError(parseError)
      setUnstackedInfo([unstackedData, unstackedColumns])
      // Project importers need this completion signal before restoring their
      // chart mapping. Without it, a late parser result can leave a valid
      // project open with an empty-looking chart configuration.
      return parseDatasetAndSetData(userData, dataTypes, {
        thousandsSeparator,
        decimalsSeparator,
        locale,
      })
    },
    [parseDatasetAndSetData, setStackDimension, setUnstackedInfo]
  )

  // ─── Public API ───────────────────────────────────────────────────────────

  return {
    userInput,
    setUserInput: setUserDataAndDetect,
    userData,
    userDataType,
    parseError,
    unstackedData,
    unstackedColumns,
    separator,
    setSeparator: handleChangeSeparator,
    thousandsSeparator,
    setThousandsSeparator: handleChangeThousandsSeparator,
    decimalsSeparator,
    setDecimalsSeparator: handleChangeDecimalSeparator,
    locale,
    setLocale: handleChangeLocale,
    stackDimension,
    dataSource,
    data,
    loading,
    coerceTypes,
    loadSample,
    handleInlineEdit,
    handleTranspose,
    handleStackOperation,
    setJsonData,
    resetDataLoader: reset,
    hydrateFromSavedProject,
    dataLoaderMode: mode,
    startDataReplace,
    cancelDataReplace,
    commitDataReplace,
    replaceRequiresConfirmation,
  }
}
